// Provenance: repo:tds-reconciliation-portal:frontend/src/components/{StatusBadge,
// CategoryBadge, FlagChip, ResolutionBadge}.tsx (ADR-0005) — one module, restyled on the
// estate tokens (ADR-0010). The predecessor's colour meanings carry; Mantine's palette
// names become Tailwind tones.
import type { Resolution } from '../api/types'
import { categoryMeta } from '../format'

export type Tone = 'gray' | 'blue' | 'green' | 'yellow' | 'red' | 'orange' | 'violet' | 'teal'

const TONES: Record<Tone, string> = {
  gray: 'bg-muted text-muted-foreground',
  blue: 'bg-blue-100 text-blue-800',
  green: 'bg-emerald-100 text-emerald-800',
  yellow: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-800',
  orange: 'bg-orange-100 text-orange-800',
  violet: 'bg-violet-100 text-violet-800',
  teal: 'bg-teal-100 text-teal-800',
}

export function Chip({
  tone = 'gray',
  children,
  title,
  solid = false,
  className = '',
}: {
  tone?: Tone
  children: React.ReactNode
  title?: string
  solid?: boolean
  className?: string
}) {
  const solidTone: Record<Tone, string> = {
    gray: 'bg-muted-foreground text-background',
    blue: 'bg-blue-600 text-white',
    green: 'bg-emerald-600 text-white',
    yellow: 'bg-amber-500 text-white',
    red: 'bg-red-600 text-white',
    orange: 'bg-orange-500 text-white',
    violet: 'bg-violet-600 text-white',
    teal: 'bg-teal-600 text-white',
  }
  return (
    <span
      title={title}
      className={`inline-flex items-center whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium ${
        solid ? solidTone[tone] : TONES[tone]
      } ${className}`}
    >
      {children}
    </span>
  )
}

const STATUS_TONES: Record<string, Tone> = {
  // runs
  queued: 'gray',
  running: 'blue',
  complete: 'green',
  failed_validation: 'red',
  failed: 'red',
  // uploads
  parsed: 'green',
  needs_mapping: 'yellow',
  // alias groups
  approved: 'green',
  proposed: 'yellow',
  retired: 'gray',
}

export function StatusBadge({ status }: { status: string }) {
  return <Chip tone={STATUS_TONES[status] ?? 'gray'}>{status.replace(/_/g, ' ')}</Chip>
}

export function CategoryBadge({ category, full = false, action }: {
  category: string
  full?: boolean
  /** the category's recommended next action (Stage E) — shown on hover so
   *  the badge answers "so what do I do about it" without costing a column */
  action?: string
}) {
  const meta = categoryMeta(category)
  return (
    <Chip tone={meta.color as Tone} title={action ? `${category} — ${action}` : category}>
      {full ? category : meta.short}
    </Chip>
  )
}

export type Flag = 'M' | 'TOL' | 'MM' | null

/** M / TOL / MM verdict chip for the reconciliation grid. TOL is accepted
 *  because it is inside the run's tolerance — allowed, but marked. */
export function FlagChip({ flag, hint }: { flag: Flag; hint?: string }) {
  if (flag === null) return <Chip tone="gray">—</Chip>
  if (flag === 'M') return <Chip tone="green" title={hint}>M</Chip>
  if (flag === 'TOL') return <Chip tone="yellow" solid title={hint}>TOL</Chip>
  return <Chip tone="red" solid title={hint}>MM</Chip>
}

const RES_META: Record<string, { tone: Tone; label: string }> = {
  open: { tone: 'gray', label: 'Open' },
  resolved_pending_review: { tone: 'yellow', label: 'Pending review' },
  approved: { tone: 'green', label: 'Approved' },
  rejected: { tone: 'red', label: 'Rejected' },
  reversal_pending: { tone: 'orange', label: 'Reversal pending' },
  reversed: { tone: 'gray', label: 'Reversed' },
}

export function ResolutionBadge({ resolution }: { resolution: Resolution | undefined }) {
  if (!resolution) {
    return <Chip tone="gray" className="border border-border bg-transparent">Unresolved</Chip>
  }
  const m = RES_META[resolution.status] ?? { tone: 'gray' as Tone, label: resolution.status }
  const tip = [
    resolution.reason_code,
    resolution.comment ?? undefined,
    resolution.status === 'open' && resolution.review_comment
      ? `Rejected: ${resolution.review_comment}`
      : undefined,
  ]
    .filter(Boolean)
    .join(' · ')
  const badge = (
    <Chip tone={resolution.orphaned ? 'red' : m.tone} title={tip || undefined}>
      {resolution.orphaned ? 'Orphaned' : m.label}
    </Chip>
  )
  // N3: a weak machine match moves figures on the least-corroborated
  // evidence, so it wears its flag wherever the resolution shows
  if (resolution.details?.flag === 'amount_only') {
    return (
      <span className="inline-flex flex-wrap gap-1">
        {badge}
        <Chip tone="orange">matched on amount alone — check</Chip>
      </span>
    )
  }
  return badge
}
